import { useEffect, useState } from "react";
import api from "@/lib/api";
import { BarChart3, Loader2, TrendingUp, AlertTriangle, CheckCircle2, GitBranch } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { STAGE_MAP } from "@/lib/constants";

export default function Analytics() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/analytics").then((r) => setData(r.data)); }, []);

  if (!data) return <div className="flex justify-center py-40"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="p-6">
      <header className="mb-6">
        <h1 className="font-heading text-4xl font-black tracking-tighter flex items-center gap-3">
          <BarChart3 className="w-8 h-8 text-primary" /> Pipeline Analytics
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Throughput, stall points and conversion to "product ready".</p>
      </header>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Kpi label="Total Ideas" value={data.total} icon={GitBranch} />
        <Kpi label="Conversion to Ready" value={`${data.conversion}%`} icon={TrendingUp} tone="success" />
        <Kpi label="SLA Breaches" value={data.breached} icon={AlertTriangle} tone={data.breached ? "danger" : "muted"} />
        <Kpi label="Bottleneck" value={data.bottleneck} icon={AlertTriangle} tone="warning" small />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="border border-border bg-card rounded-sm p-4">
          <h3 className="font-heading font-bold tracking-tight mb-4">Active ideas per stage</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.stage_data}>
              <XAxis dataKey="key" tickFormatter={(k) => STAGE_MAP[k]?.short || k} tick={{ fill: "#71717A", fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={50} />
              <YAxis allowDecimals={false} tick={{ fill: "#71717A", fontSize: 11 }} />
              <Tooltip contentStyle={{ background: "#111", border: "1px solid #262626", borderRadius: 4, fontSize: 12 }} labelFormatter={(k) => STAGE_MAP[k]?.name || k} />
              <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                {data.stage_data.map((d) => <Cell key={d.key} fill={STAGE_MAP[d.key]?.color || "#38BDF8"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="border border-border bg-card rounded-sm p-4">
          <h3 className="font-heading font-bold tracking-tight mb-4">Avg. days in stage (stall points)</h3>
          <div className="space-y-2">
            {data.stage_data.map((d) => (
              <div key={d.key} className="flex items-center gap-3">
                <span className="w-28 text-xs text-muted-foreground shrink-0">{STAGE_MAP[d.key]?.short}</span>
                <div className="flex-1 h-5 bg-secondary rounded-sm overflow-hidden">
                  <div className="h-full rounded-sm" style={{ width: `${Math.min(d.avg_age * 8, 100)}%`, background: STAGE_MAP[d.key]?.color }} />
                </div>
                <span className="font-mono text-xs w-12 text-right">{d.avg_age}d</span>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-4 gap-2 mt-6 pt-4 border-t border-border">
            {Object.entries(data.counts).map(([k, v]) => (
              <div key={k} className="text-center">
                <p className="font-mono text-xl font-bold">{v}</p>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground capitalize">{k}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, tone = "muted", small }) {
  const color = { danger: "#FF3B30", warning: "#F59E0B", success: "#22C55E", muted: "#fff" }[tone];
  return (
    <div className="border border-border bg-card rounded-sm p-4">
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
        <Icon className="w-4 h-4" style={{ color: tone === "muted" ? "#A1A1AA" : color }} />
      </div>
      <p className={`font-heading font-black mt-1 ${small ? "text-lg" : "text-3xl"}`} style={{ color }}>{value}</p>
    </div>
  );
}
